import { useState } from 'react';
import { toast } from 'sonner';
import { Button, ScrollTopButton } from '@indipia/ui-core';
import { ThumbImg } from '../components/ThumbImg';
import { PhotoViewerLayer } from '../components/PhotoViewerLayer';
import type { BundleShareViewData } from '../api/photoApi';
import { API_PRX } from '../api/endpoints';

interface LinkPhoto {
  id: number;
  color: string;
  thumbUrl?: string | null;
  originalUrl?: string | null;
  takenAt?: string | null;
  registeredAt?: string | null;
}

interface ViewerPhoto {
  photoId: number;
  thumbUrl: string | null;
  originalUrl: string | null;
  takenAt: string | null;
  tags: { tagType: string; tagValue: string }[];
}

interface DateGroup {
  date: string;
  photos: LinkPhoto[];
}

interface SiteGroup {
  siteId: number;
  siteName: string;
  dateGroups: DateGroup[];
}

export interface ShareLinkMainProps {
  data?: BundleShareViewData;
  isPreview?: boolean;
  previewToken?: string;
  shareId?: number | null;
}

const THUMB_COLORS = [
  'var(--gt-subtle)',
  'var(--shell-skeleton)',
  'var(--shell-bg)',
  'var(--shell-surface)',
  'var(--shell-skeleton)',
  'var(--gt-subtle)',
];

function fmtDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return `${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function buildSiteGroups(data: BundleShareViewData): SiteGroup[] {
  const siteNameMap = new Map((data.sites ?? []).map(s => [s.siteId, s.siteName]));
  const siteMap = new Map<number, { siteName: string; dateMap: Map<string, LinkPhoto[]> }>();
  let colorIdx = 0;
  for (const p of data.photos) {
    const siteId = p.siteId ?? 0;
    const siteName = siteId > 0 ? (siteNameMap.get(siteId) ?? `현장 #${siteId}`) : '공유 사진';
    if (!siteMap.has(siteId)) siteMap.set(siteId, { siteName, dateMap: new Map() });
    const site = siteMap.get(siteId)!;
    const dateKey = fmtDate(p.takenAt);
    if (!site.dateMap.has(dateKey)) site.dateMap.set(dateKey, []);
    site.dateMap.get(dateKey)!.push({
      id: p.photoId,
      color: THUMB_COLORS[colorIdx++ % THUMB_COLORS.length],
      thumbUrl: p.thumbUrl,
      originalUrl: p.originalUrl,
      takenAt: p.takenAt,
      registeredAt: p.registeredAt,
    });
  }
  return Array.from(siteMap.entries()).map(([siteId, { siteName, dateMap }]) => ({
    siteId,
    siteName,
    dateGroups: Array.from(dateMap.entries()).map(([date, photos]) => ({ date, photos })),
  }));
}

// ── 아이콘 ────────────────────────────────────────────────────
function IcCamera() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
function IcClock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function IcLock() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
function IcDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function IcHome() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

// <a download> 속성 방식은 카카오톡 인앱브라우저 등 일부 WebView에서 문제(iOS는 아예 미지원 —
// devtalk.kakao.com/t/topic/146168, 반복 확인창 원인 후보)가 있어, 서버가 Content-Disposition:
// attachment 헤더로 내려주는 HTTP 헤더 방식(카카오 공식 권장)을 대신 사용한다.
// download 속성은 그대로 남겨둔다 — 헤더가 없는 환경(구형 브라우저 등)의 폴백으로도 동작한다.
function withDownloadParam(url: string): string {
  return url + (url.includes('?') ? '&' : '?') + 'download=1';
}

function triggerAnchorDownload(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function anchorDownload(url: string, filename: string) {
  triggerAnchorDownload(withDownloadParam(url), filename);
}

async function downloadPhoto(photo: LinkPhoto, filename: string) {
  const url = photo.originalUrl ?? photo.thumbUrl;
  if (!url) return;

  if (isTouchDevice() && typeof navigator.share === 'function') {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
    } catch { /* fall through */ }
  }

  anchorDownload(url, filename);
}

// 여러 장을 개별 다운로드로 저장하면(Web Share API 포함) 기기·브라우저마다 확인창이 사진
// 수만큼 반복되거나(카카오톡 인앱브라우저 등 — 실기기 검증됨), navigator.share의 개수·용량
// 제한(Chromium 기준 10개·50MB)에 걸려 여러 번 공유시트가 뜨는 등 환경별로 동작이 갈린다.
// 그래서 항상 ZIP 하나로 묶어 다운로드 이벤트를 1번만 발생시킨다.
//
// zip은 서버(share-bundles/.../zip)가 생성해 Content-Disposition 헤더로 내려준다 — 클라이언트에서
// JSZip으로 blob을 만들어 내려받으면 카카오톡 인앱브라우저 등 일부 WebView가 blob: URL을 못 읽어
// 조용히 실패한다(실기기 검증됨. DownloadManager로 넘기는 구조라 실제 URL만 처리 가능,
// 일반 브라우저는 blob을 프로세스 내에서 바로 저장하므로 문제 없음). 개별 사진 다운로드와
// 동일하게 실제 HTTP URL을 써서 이 문제를 원천적으로 피한다.
// BE(ShareBundleController.buildZipFilename)와 동일한 형식 — Safari는 서버 Content-Disposition
// 헤더보다 <a download> 속성값을 우선하는 걸로 확인됨(실기기 검증). 두 쪽 형식이 다르면 브라우저에
// 따라 파일명이 달라지므로, 헤더가 무시되는 경우를 대비해 여기서도 같은 이름을 미리 만들어둔다.
function sanitizeFilenamePart(s: string): string {
  return s.replace(/[\\/:*?"<>|\x00-\x1f]/g, ' ').trim();
}

function buildZipFilename(title: string | null | undefined, sentDtm: string | null | undefined): string {
  const safeTitle = sanitizeFilenamePart(title && title.trim() ? title : '사진 공유');
  const d = sentDtm ? new Date(sentDtm) : new Date();
  const dateStr = isNaN(d.getTime())
    ? ''
    : `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  return `[인디포토] ${safeTitle}_${dateStr}.zip`;
}

function downloadPhotos(shareId: number | null | undefined, photos: LinkPhoto[], title: string | null | undefined, sentDtm: string | null | undefined) {
  if (!photos.length) return;
  if (!shareId) {
    toast.error('공유 링크 정보를 확인할 수 없어 다운로드에 실패했습니다');
    return;
  }
  const base = `${import.meta.env.VITE_API_BASE || ''}/api${API_PRX.PHOTO}/share-bundles/by-share/${shareId}/zip`;
  const url = `${base}?photoIds=${photos.map(p => p.id).join(',')}`;
  triggerAnchorDownload(url, buildZipFilename(title, sentDtm));
}

// ─────────────────────────────────────────────────────────────
export function ShareLinkMain({ data, isPreview = false, previewToken, shareId }: ShareLinkMainProps) {
  const [selectedGroups, setSelectedGroups] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [viewer, setViewer] = useState<{ photos: ViewerPhoto[]; index: number } | null>(null);

  const openViewer = (group: SiteGroup, photoId: number) => {
    const flat = group.dateGroups.flatMap(dg => dg.photos);
    const photos: ViewerPhoto[] = flat.map(p => ({
      photoId: p.id,
      thumbUrl: p.thumbUrl ?? null,
      originalUrl: p.originalUrl ?? null,
      takenAt: p.takenAt ?? null,
      tags: [],
    }));
    const index = flat.findIndex(p => p.id === photoId);
    setViewer({ photos, index: index >= 0 ? index : 0 });
  };

  const siteGroups: SiteGroup[] = data ? buildSiteGroups(data) : [];
  const displayMsg  = data?.msgContent ?? '';
  const displayDate = data ? fmtDateTime(data.sentDtm) : '-';
  const totalPhotos = data?.totalCount ?? 0;

  const toggleGroup = (siteId: number) => {
    setSelectedGroups(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
      return next;
    });
  };

  const toggleCollapse = (siteId: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
      return next;
    });
  };

  const selectedCount = siteGroups
    .filter(g => selectedGroups.has(g.siteId))
    .reduce((sum, g) => sum + g.dateGroups.reduce((s, d) => s + d.photos.length, 0), 0);

  return (
    <>
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, fontFamily: "-apple-system,'Apple SD Gothic Neo','Noto Sans KR',sans-serif" }}>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

        {/* ── 다크 헤더 ── */}
        <div style={{ background: 'var(--gray-900)', padding: '18px 28px 24px' }}>

          {/* 로고 행 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <img src="/brand/indipia-logo-dark.svg" alt="INDIPIA" style={{ height: 24, flexShrink: 0 }} />
            </div>
            {!isPreview && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    if (selectedCount === 0) return;
                    const photos = siteGroups
                      .filter(g => selectedGroups.has(g.siteId))
                      .flatMap(g => g.dateGroups.flatMap(d => d.photos));
                    downloadPhotos(shareId, photos, data?.msgTitle, data?.sentDtm);
                  }}
                  disabled={selectedCount === 0}
                  style={{ background: selectedCount > 0 ? 'var(--lavender-500)' : 'color-mix(in srgb, var(--lavender-500) 40%, transparent)' }}
                >
                  <IcDownload /> 선택 현장 사진 저장
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const photos = siteGroups.flatMap(g => g.dateGroups.flatMap(d => d.photos));
                    downloadPhotos(shareId, photos, data?.msgTitle, data?.sentDtm);
                  }}
                  style={{ border: '1.5px solid var(--alpha-white-300)', color: 'var(--gray-0)' }}
                >
                  <IcDownload /> 전체 사진 저장
                </Button>
              </div>
            )}
          </div>

          {/* 제목 */}
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-0)', letterSpacing: '-0.6px', marginBottom: 12 }}>
            {data?.msgTitle ?? '공유된 사진입니다'}
          </div>

          {/* 메타 정보 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--type-caption-size)', color: 'var(--alpha-white-600)' }}>
              <IcCamera /> 사진 {totalPhotos}장
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--type-caption-size)', color: 'var(--alpha-white-600)' }}>
              <IcClock /> 공유 {displayDate}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 'var(--type-caption-size)', color: 'var(--alpha-white-600)' }}>
              <IcLock /> 링크 유효 {data ? fmtDateTime(data.expiresDtm) : '-'}
            </span>
          </div>
        </div>

        {/* ── 콘텐츠 영역 ── */}
        <div style={{ background: 'var(--shell-bg)', minHeight: '100%', padding: '20px 28px 48px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* 메시지 카드 — 미리보기 전용 */}
          {isPreview && displayMsg && (
            <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', display: 'flex', alignItems: 'flex-start', gap: 14, boxShadow: '0 1px 6px var(--alpha-black-50)' }}>
              <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: 'var(--gt-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, color: 'var(--gray-0)', flexShrink: 0 }}>
                📷
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: 'var(--shell-hint)', marginBottom: 8 }}>
                  {data?.senderName ?? ''} 사장님의 메시지
                </div>
                <div style={{ fontSize: 14, color: 'var(--shell-on-surface)', lineHeight: 1.75, whiteSpace: 'pre-line' }}>
                  {displayMsg}
                </div>
              </div>
            </div>
          )}

          {/* 현장별 사진 그룹 */}
          {siteGroups.length === 0 && (
            <div style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', padding: '40px 0', textAlign: 'center' as const, fontSize: 13, color: 'var(--shell-hint)' }}>
              공유된 사진이 없습니다.
            </div>
          )}
          {siteGroups.map(group => {
            const isCollapsed = collapsed.has(group.siteId);
            const isSelected  = selectedGroups.has(group.siteId);
            const groupTotal  = group.dateGroups.reduce((s, d) => s + d.photos.length, 0);

            return (
              <div key={group.siteId} style={{ background: 'var(--shell-surface)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: '0 1px 6px var(--alpha-black-50)' }}>

                {/* 그룹 헤더 */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', gap: 10 }}>
                  {/* 체크박스 */}
                  <div
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    onClick={() => toggleGroup(group.siteId)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(group.siteId); } }}
                    style={{ width: 20, height: 20, borderRadius: 'var(--radius-sm)', border: `1.5px solid ${isSelected ? 'var(--gt-primary)' : 'var(--shell-border)'}`, background: isSelected ? 'var(--gt-primary)' : 'var(--shell-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all .15s' }}
                  >
                    {isSelected && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--gray-0)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
                  </div>

                  {/* 홈 아이콘 */}
                  <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', background: 'var(--gt-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IcHome />
                  </div>

                  <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--shell-on-surface)', letterSpacing: '-0.3px', flex: 1 }}>{group.siteName}</span>

                  {/* 장수 배지 */}
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 'var(--radius-full)', background: 'var(--shell-skeleton)', color: 'var(--shell-on-surface-2)' }}>{groupTotal}장</span>

                  {/* 접기/펼치기 */}
                  <button
                    onClick={() => toggleCollapse(group.siteId)}
                    aria-label={isCollapsed ? '펼치기' : '접기'}
                    style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>
                      <polyline points="18 15 12 9 6 15" />
                    </svg>
                  </button>
                </div>

                {/* 날짜별 사진 */}
                {!isCollapsed && group.dateGroups.map((dg, di) => (
                  <div key={di} style={{ borderTop: '1px solid var(--shell-border)' }}>
                    {/* 날짜 라벨 */}
                    <div style={{ padding: '10px 18px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-hint)' }}>촬영일</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--shell-on-surface)' }}>{dg.date}</span>
                      <span style={{ fontSize: 12, color: 'var(--shell-hint)', fontWeight: 600 }}>{dg.photos.length}장</span>
                    </div>

                    {/* 2열 사진 그리드 */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, padding: '0 18px 18px' }}>
                      {dg.photos.map(photo => (
                        <div
                          key={photo.id}
                          onClick={() => openViewer(group, photo.id)}
                          style={{ position: 'relative', borderRadius: 'var(--radius-md)', overflow: 'hidden', background: photo.color, aspectRatio: '4/3', cursor: 'pointer' }}
                        >
                          <ThumbImg url={photo.thumbUrl ?? null} previewToken={previewToken} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} fallback={<div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg></div>} />
                          {/* 날짜 + 저장 오버레이 */}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to bottom, transparent, var(--alpha-black-700))', padding: '20px 8px 7px', display: 'flex', alignItems: 'flex-end', gap: 6 }}>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--alpha-white-600)', minWidth: 22 }}>등록일</span>
                                <span style={{ fontSize: 12, color: 'var(--alpha-white-900)' }}>{fmtDate(photo.registeredAt)}</span>
                              </div>
                            </div>
                            {!isPreview && (
                              <button
                                onClick={e => { e.stopPropagation(); void downloadPhoto(photo, `photo-${photo.id}.jpg`); }}
                                aria-label="사진 저장"
                                style={{ width: 30, height: 30, borderRadius: 'var(--radius-sm)', flexShrink: 0, border: '1.5px solid var(--alpha-white-600)', background: 'var(--alpha-white-200)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, color: 'var(--gray-0)' }}
                              >
                                <IcDownload />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          {/* 전체 사진 수 요약 */}
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--shell-hint)', paddingTop: 4 }}>
            총 {totalPhotos}장의 사진이 공유되었습니다
          </div>
        </div>

        {/* 스크롤 맨 위로 (§15.55) — 바깥은 100dvh overflow:hidden 이고 스크롤은 이 div 가 갖는다.
            버튼이 이 div 를 스크롤 소유자로 찾도록 바로 안쪽에 둔다. */}
        <ScrollTopButton />

      </div>
    </div>

    {viewer && (
      <PhotoViewerLayer
        photos={viewer.photos}
        initialIndex={viewer.index}
        onClose={() => setViewer(null)}
        previewToken={previewToken}
      />
    )}
    </>
  );
}
